import { v4 as uuidv4 } from 'uuid';
import {
    ContentPropertiesManager,
    FontFamily,
    getAllElementsRecursively,
    SpecificationExporter,
    Template,
    TextElement,
    TextStyles,
    VideoWebSpecificationParser,
    TextStyle,
    CreativeTypes,
    ControllableElement,
    ContentPropertyData,
} from '@bynder-studio/render-core';
import { PlaybackManipulationRenderer } from '@bynder-studio/render-web';
import { useSelector } from 'react-redux';
import { fontsElementsSelector } from 'packages/store/creativeEditor/creativeEditor.selectors';
import { Account, ActionStatus, ColorPaletteAction } from 'packages/hooks/useAccessRights/types';
import { syncBrandColors } from 'packages/pages/editor/syncBrandColors';
import { colorPalettes } from 'packages/store/brandColors/brandColors.selectors';
import useAccessRights from 'packages/hooks/useAccessRights';
import { Component } from 'packages/services/ComponentService';
import { prepareDataForSave } from '~/common/editor/editor.bloc';
import useEditor from '~/hooks/useEditor';
import { getContentPropertiesFromElement, convertTextStyles, replaceDuplicatedIds } from './utils';

export interface PreparedComponent {
    elements: Record<string, unknown>[];
    textStyles?: TextStyle[];
    contentProperties: ContentPropertyData[];
    type: CreativeTypes;
}

const assignNewUuid = <T extends { uuid: string }>(items: T[]): Map<string, string> => {
    const idsMap = new Map<string, string>();

    items.map((item) => {
        const id: string = idsMap.get(item.uuid) || uuidv4();
        idsMap.set(item.uuid, id);
        item.uuid = id;
    });

    return idsMap;
};

const changeElementsContentPropertyIds = <T extends { contentPropertyId?: string }>(
    elements: T[],
    idsMap: Map<string, string>,
) => {
    getAllElementsRecursively(elements).forEach((element) => {
        if (element.contentPropertyId && idsMap.has(element.contentPropertyId)) {
            element.contentPropertyId = idsMap.get(element.contentPropertyId);
        }
    });
};

const useComponents = () => {
    const { palettes } = useSelector(colorPalettes);
    const fonts: FontFamily[] = useSelector(fontsElementsSelector);
    const { creativeModel, manipulationRenderer, creativeType } = useEditor();
    const { isAccountActionAllowed } = useAccessRights();

    const createComponent = (selectedElements: ControllableElement[]): PreparedComponent => {
        if (manipulationRenderer instanceof PlaybackManipulationRenderer) {
            manipulationRenderer.pausePlayback();
        }

        const textStyles = creativeModel.getTextStyles() as TextStyles;
        const [idsMatchList, stylesList] = convertTextStyles(selectedElements, textStyles.list);

        const specElements = SpecificationExporter.exportElements(selectedElements) as unknown as Template['elements'];
        Object.values(specElements).forEach((element) => {
            if (element) {
                delete (element as any).parentId;
            }
        });

        const contentProperties = getContentPropertiesFromElement(
            creativeModel,
            Object.values(specElements),
            idsMatchList,
        );

        const syncedSpecElements = replaceDuplicatedIds(specElements, idsMatchList);

        const { elements } = prepareDataForSave(false, syncedSpecElements, {}, 0, creativeType);

        const baseComponent = {
            elements: (elements || []) as Record<string, unknown>[],
            contentProperties,
            type: creativeType as unknown as CreativeTypes,
        };

        if (stylesList.length > 0) {
            return {
                ...baseComponent,
                textStyles: stylesList,
            };
        }

        return baseComponent;
    };

    const applyComponent = (preset: Component, isUniqueContentProperties = false) => {
        const startFrame =
            manipulationRenderer instanceof PlaybackManipulationRenderer ? manipulationRenderer.getCurrentFrame() : 0;
        const videoWebSpecificationParser = new VideoWebSpecificationParser();
        const parsedElements = videoWebSpecificationParser.parseElements(preset as any, fonts);

        const textStyles = creativeModel.getTextStyles() as TextStyles;
        const updatedTextStylesIds = textStyles.extendStyles(preset.textStyles ?? []);

        const contentProperties = preset.contentProperties ?? [];

        const contentPropertiesManager = creativeModel.getContentPropertiesManager() as ContentPropertiesManager;
        if (isUniqueContentProperties) {
            const idsMap = assignNewUuid(contentProperties);
            contentPropertiesManager.adjustSimilarContentPropertiesNames(contentProperties);
            changeElementsContentPropertyIds(parsedElements, idsMap);
        }

        const updatedContentPropertyIds = contentPropertiesManager.extendContentProperties(
            contentProperties,
            fonts,
            updatedTextStylesIds,
            isUniqueContentProperties,
        );

        getAllElementsRecursively(parsedElements).forEach((element: any) => {
            if (updatedContentPropertyIds.has(element.contentPropertyId)) {
                element.contentPropertyId = updatedContentPropertyIds.get(element.contentPropertyId);
            }

            if (element instanceof TextElement) {
                element.textStyles = element.textStyles.filter((styleId) => textStyles.getStyle(styleId));
                element.formattedText.runs.forEach((run) => {
                    if (!run.styleId) {
                        return;
                    }

                    const newId = updatedTextStylesIds.get(run.styleId);

                    if (newId) {
                        run.styleId = newId;
                    }
                });
            }
        });

        creativeModel.addElements(parsedElements, startFrame);

        if (
            isAccountActionAllowed(ColorPaletteAction.EDIT_COLOR_PALETTE, Account.COLOR_PALETTES) ===
            ActionStatus.ALLOWED
        ) {
            syncBrandColors(creativeModel, palettes, () => null);
        }
    };

    return {
        applyComponent,
        createComponent,
    };
};

export default useComponents;
